import { ReactElement, useState, useCallback, useEffect, useRef } from 'react';

import { ref, uploadBytes } from 'firebase/storage';
import { UploadResult } from 'firebase/storage';

import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

import { ActionProps } from '../../contextProviders/AppProvider';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useData } from '../../contextProviders/DataProvider';
import useWeatherLocations from '../../dataHooks/useWeatherLocations';
import useCourseUsgaMutators from '../../dataMutators/useCourseUsgaMutators';
import { useWeatherLocationMutators } from '../../dataMutators/useWeatherLocationMutators';
import { storage } from '../../firebase';
import { useAutoSave } from '../../stateHooks/useAutoSave';
import Address, { ADDRESS_TYPES } from '../../types/Address';
import Course, {
  ALL_COURSE_MEMBERSHIP_TYPES,
  ALL_COURSE_OWNERSHIP_TYPES,
  CourseMembershipType,
  CourseOwnershipType,
  ScorecardImage,
  USGA_COURSE_IMPORT_STATUSES,
} from '../../types/Course';
import { DefaultObjects } from '../../types/DefaultObjects';
import { WeatherLocation } from '../../types/WeatherLocation';
import { PASSWORD_MANAGER_IGNORE_INPUT_PROPS } from '../../utils/formInputProps';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { formatEnumLabel } from '../../utils/utils';
import AddressEditor from '../address/AddressEditor';
import GratiImageUpload from '../common/GratiImageUpload';
import GratiMap from '../common/GratiMap';
import OrgSelector from '../org/OrgSelector';

import CourseScorecardEditor from './CourseScorecardEditor';
import CourseTeeEditor from './CourseTeeEditor';
import CourseUsgaTeeView from './CourseUsgaTeeView';

export interface CourseEditorProps {
  course: Course | undefined;
  isNew?: boolean;
  /** A new course seeded from the USGA (CourseCreatePage's import mode): the action reads "Import". */
  isImport?: boolean;
  handleCreate?: (course: Course) => void;
  handleDelete?: (course: Course) => void;
  updateCourse?: (course: Partial<Course>) => Promise<void>;
  onActionsChange?: (pageActions: ActionProps[] | null, godActions: ReactElement | null) => void;
}

type WeatherGroupOption = WeatherLocation | { inputValue: string; name: string };

const weatherGroupFilter = createFilterOptions<WeatherGroupOption>();

export default function CourseEditor(props: CourseEditorProps): ReactElement {
  const {
    isNew = false,
    isImport = false,
    handleCreate,
    handleDelete,
    updateCourse,
    onActionsChange,
  } = props;
  const { isGod } = useAuth();
  const { isOrgAdmin } = useData();
  const { weatherLocations } = useWeatherLocations();
  const { createWeatherLocation } = useWeatherLocationMutators();
  const { requestUsgaImport } = useCourseUsgaMutators();
  // Written by the server (handleCourseUsgaImportRequested), so read from the live course, not the
  // editor's local copy.
  const usgaImport = props.course?.usgaImport ?? null;
  const usgaSeason = props.course?.usgaSeason ?? null;
  const isUsgaImportRunning = usgaImport?.status === USGA_COURSE_IMPORT_STATUSES.REQUESTED
    || usgaImport?.status === USGA_COURSE_IMPORT_STATUSES.RUNNING;

  const [course, setCourse] = useState<Course>(props.course ?? DefaultObjects.Course);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // The USGA course id being typed in, to link the course (or change a wrong link); null when the
  // field is closed on a linked course.
  const [usgaCourseIdInput, setUsgaCourseIdInput] = useState<string | null>(null);
  const isUsgaCourseIdValid = !!usgaCourseIdInput && /^\d+$/.test(usgaCourseIdInput.trim())
    && usgaCourseIdInput.trim() !== course.usgaCourseId;

  const handleLinkUsgaCourse = async () => {
    if (!usgaCourseIdInput || !isUsgaCourseIdValid) return;
    const usgaCourseId = usgaCourseIdInput.trim();
    // Written straight to the course with the import request, so only the local copy needs it.
    setCourse((prev) => ({ ...prev, usgaCourseId }));
    setUsgaCourseIdInput(null);
    await requestUsgaImport(course.itemId, usgaCourseId);
  };

  // Sync tees from Firestore when new tee IDs arrive (e.g. from USGA import bypassing local state).
  useEffect(() => {
    const incoming = props.course;
    if (!incoming?.tees?.length) return;
    setCourse(prev => {
      const prevIds = new Set(prev.tees?.map((t) => t.itemId) ?? []);
      const hasNewTees = incoming.tees!.some((t) => !prevIds.has(t.itemId));
      if (!hasNewTees) return prev;
      return {
        ...prev,
        tees: incoming.tees,
        defaultTeeId: incoming.defaultTeeId,
        defaultMensTeeId: incoming.defaultMensTeeId,
        defaultWomensTeeId: incoming.defaultWomensTeeId,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.course?.tees?.length]);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  // Auto-save hook (only active for edit mode)
  const { queueChange } = useAutoSave<Course>({
    updateFn: updateCourse,
    itemId: course.itemId,
    enabled: !isNew && !!updateCourse,
  });

  // Handle field changes with auto-save for edit mode
  const handleFieldChange = <K extends keyof Course>(field: K, value: Course[K]) => {
    setCourse((prev) => ({ ...prev, [field]: value }));
    queueChange(field, value);
  };

  // Handle tee data changes (may update multiple fields at once)
  const handleTeeDataChange = useCallback((updates: Partial<Course>) => {
    setCourse((prev) => ({ ...prev, ...updates }));
    for (const [field, value] of Object.entries(updates)) {
      queueChange(field as keyof Course, value as Course[keyof Course]);
    }
  }, [queueChange]);

  const handleImageUpdated = (snapshot: UploadResult | null) => {
    if (course) {
      const logo = snapshot ? snapshot.ref.name : '';
      setCourse({ ...course, logo });
      // Image upload already saved to storage, just update the course doc
      if (!isNew && updateCourse) {
        updateCourse({ itemId: course.itemId, logo });
      }
    }
  };

  const handleFileSelected = (file: File | null) => {
    setPendingLogoFile(file);
  };

  const handleScorecardImagesChanged = (images: ScorecardImage[]) => {
    setCourse((prev) => ({ ...prev, scorecardImages: images }));
    if (!isNew && updateCourse) {
      updateCourse({ itemId: course.itemId, scorecardImages: images });
    }
  };

  const onSubmit = async () => {
    if (!handleCreate) return;
    try {
      let courseToSave = course;

      // If there's a pending logo file (new course with deferred upload), upload it now
      if (pendingLogoFile && isNew) {
        const logoRef = ref(storage, `course/${courseToSave.itemId}/publicFile/${courseToSave.itemId}-logo`);
        const snapshot = await uploadBytes(logoRef, pendingLogoFile);
        courseToSave = { ...courseToSave, logo: snapshot.ref.name };
      }

      handleCreate(courseToSave);
    } catch (error) {
      console.error('Error saving course:', error);
    }
  };

  const handleStreetAddressChanged = useCallback((address: Address) => {
    setCourse((prev) => {
      const updated = {
        ...prev,
        streetAddress: address,
        // If the address has a name (like "Awbrey Glen Golf Club"), use it as the course name
        name: address.name || prev.name,
      };
      if (!isNew && updateCourse) {
        updateCourse({ itemId: prev.itemId, streetAddress: address, name: updated.name });
      }
      return updated;
    });
  }, [isNew, updateCourse]);

  const handleWeatherGroupChanged = useCallback(async (option: WeatherGroupOption | null) => {
    if (!option) {
      setCourse((prev) => ({ ...prev, weatherLocationId: undefined }));
      if (!isNew && updateCourse) updateCourse({ itemId: course.itemId, weatherLocationId: null as unknown as undefined });
      return;
    }
    let locationId: string;
    if ('inputValue' in option) {
      const created = await createWeatherLocation(option.inputValue, course.itemId);
      if (!created) return;
      locationId = created;
    } else {
      locationId = option.itemId;
    }
    setCourse((prev) => ({ ...prev, weatherLocationId: locationId }));
    if (!isNew && updateCourse) updateCourse({ itemId: course.itemId, weatherLocationId: locationId });
  }, [course.itemId, createWeatherLocation, isNew, updateCourse]);

  const handleMailingAddressChanged = useCallback((address: Address) => {
    setCourse((prev) => {
      const updated = { ...prev, mailingAddress: address };
      if (!isNew && updateCourse) {
        updateCourse({ itemId: prev.itemId, mailingAddress: address });
      }
      return updated;
    });
  }, [isNew, updateCourse]);

  // Store callbacks in refs to avoid infinite loops in useEffect
  const onActionsChangeRef = useRef(onActionsChange);

  useEffect(() => {
    onActionsChangeRef.current = onActionsChange;
  });

  // Build page actions
  useEffect(() => {
    if (!onActionsChangeRef.current) return;

    const pageActions: ActionProps[] | null = isNew ? [
      { name: isImport ? 'Import' : 'Create', icon: SaveIcon, type: 'submit', form: 'course-editor-form', disabled: !course.name.trim(), variant: 'contained', color: 'primary' },
    ] : null;

    onActionsChangeRef.current(pageActions, null);
  }, [course, isImport, isNew]);

  if (course) {
    if (isGod || (course.orgId && isOrgAdmin(course.orgId))) {
      return (
        <Box
          id="course-editor-form"
          component="form"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          data-form-type="other"
          data-protonpass-ignore
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{
                justifyContent: "space-between",
                alignItems: "flex-start"
              }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <AddressEditor
                  address={course.streetAddress}
                  onAddressChanged={handleStreetAddressChanged}
                  defaultCountryCode="US"
                  label="Course Location"
                  type={ADDRESS_TYPES.STREET}
                />
              </Box>
              {course.streetAddress?.googlePlaceId &&
                <GratiMap
                  placeId={course.streetAddress.googlePlaceId}
                  sx={{
                    height: 200,
                    width: { xs: '100%', md: '50%' },
                    flexShrink: 0
                  }}/>
              }
            </Stack>
            {isGod && (
              <OrgSelector
                value={course.orgId ?? null}
                onChange={(orgPair) => {
                  setCourse((prev) => ({ ...prev, orgId: orgPair.orgId, orgName: orgPair.orgName }));
                  if (!isNew && updateCourse) {
                    updateCourse({ itemId: course.itemId, orgId: orgPair.orgId, orgName: orgPair.orgName });
                  }
                }}
                alwaysShowDropdown
                allowNone
              />
            )}
            {isGod && (
              <Autocomplete<WeatherGroupOption, false, false, true>
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                options={weatherLocations as WeatherGroupOption[]}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : 'inputValue' in option ? option.inputValue : option.name
                }
                filterOptions={(options, params) => {
                  const filtered = weatherGroupFilter(options, params);
                  if (params.inputValue.trim()) {
                    filtered.push({ inputValue: params.inputValue.trim(), name: `Create "${params.inputValue.trim()}"` });
                  }
                  return filtered;
                }}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                  return <li key={key} {...rest}>{'inputValue' in option ? option.name : option.name}</li>;
                }}
                value={weatherLocations.find((loc) => loc.itemId === course.weatherLocationId) ?? null}
                onChange={(_e, newValue) => {
                  if (typeof newValue === 'string') return;
                  handleWeatherGroupChanged(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Weather Group"
                    aria-label="Weather location group for this course"
                    slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps.htmlInput, ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS } }}
                  />
                )}
              />
            )}
            <TextField
              id="name"
              label="Course Name"
              slotProps={{ htmlInput: PASSWORD_MANAGER_IGNORE_INPUT_PROPS }}
              aria-label="Course name"
              aria-required="true"
              required
              value={course.name}
              onChange={({ target }) => handleFieldChange('name', target.value)}
            />
            <TextField
              id="phone"
              label="Phone"
              slotProps={{ htmlInput: PASSWORD_MANAGER_IGNORE_INPUT_PROPS }}
              aria-label="Course phone number"
              aria-required="false"
              value={course.phone || ''}
              onChange={({ target }) => handleFieldChange('phone', target.value || undefined)}
            />
            <TextField
              id="shortName"
              label="Short Name"
              slotProps={{ htmlInput: PASSWORD_MANAGER_IGNORE_INPUT_PROPS }}
              aria-label="Abbreviated course name"
              aria-required="true"
              value={course.shortName}
              onChange={({ target }) => handleFieldChange('shortName', target.value)}
            />
            <TextField
              id="description"
              label="Description"
              slotProps={{ htmlInput: PASSWORD_MANAGER_IGNORE_INPUT_PROPS }}
              aria-label="Course description"
              aria-required="false"
              value={course.description}
              onChange={({ target }) => handleFieldChange('description', target.value)}
            />
            <TextField
              id="membership"
              label="Membership"
              aria-label="Course membership type"
              select
              value={course.membership || ''}
              onChange={({ target }) => handleFieldChange('membership', (target.value || undefined) as CourseMembershipType | undefined)}
            >
              {ALL_COURSE_MEMBERSHIP_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {formatEnumLabel(type)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="ownership"
              label="Ownership"
              aria-label="Course ownership type"
              select
              value={course.ownership || ''}
              onChange={({ target }) => handleFieldChange('ownership', (target.value || undefined) as CourseOwnershipType | undefined)}
            >
              {ALL_COURSE_OWNERSHIP_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {formatEnumLabel(type)}
                </MenuItem>
              ))}
            </TextField>
            <GratiImageUpload
              onImageUpdated={isNew ? undefined : handleImageUpdated}
              onFileSelected={isNew ? handleFileSelected : undefined}
              deferUpload={isNew}
              storageImageFileName={course.itemId + '-logo'}
              storageImageLocation={`/course/${course.itemId}/publicFile`}
              isInitialized={!isNew && Boolean(course.logo)}
              label="Logo"
            />
            {!isNew && (
              <CourseScorecardEditor
                courseId={course.itemId}
                scorecardImages={course.scorecardImages ?? []}
                onScorecardImagesChanged={handleScorecardImagesChanged}
              />
            )}
            <TextField
              id="teeTimeSpacing"
              label="Tee Time Interval"
              aria-label="Time between tee times."
              aria-required="true"
              slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'numeric', pattern: '[0-9]*' } }}
              value={course.teeTimeSpacing}
              onChange={({ target }) => handleFieldChange('teeTimeSpacing', parseInt(target.value, 10) || 0)}
            />
            <TextField
              id="par"
              label="Par"
              aria-label="Course Par"
              aria-required="false"
              slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'numeric', pattern: '[0-9]*' } }}
              value={course.par}
              onChange={({ target }) => handleFieldChange('par', parseInt(target.value, 10) || 0)}
            />
            {(!isNew || (course.tees?.length ?? 0) > 0) && (
              <CourseTeeEditor
                course={course}
                isAdmin={isGod || !!(course.orgId && isOrgAdmin(course.orgId))}
                onTeeDataChange={handleTeeDataChange}
              />
            )}
            <AddressEditor
              address={course.mailingAddress}
              onAddressChanged={handleMailingAddressChanged}
              defaultCountryCode="US"
              label="Mailing Address"
              type={ADDRESS_TYPES.MAILING}
            />

            {isNew && course.usgaCourseId && (
              <Typography variant="bodySmall" color="text.secondary">
                From the USGA: {HANDICAP_LABELS.COURSE_ID} {course.usgaCourseId}, {course.tees?.length ?? 0} tee sets,
                posting season {!course.usgaSeason
                  ? 'unknown'
                  : course.usgaSeason.isAllYear
                    ? 'all year'
                    : `${course.usgaSeason.startMonthDay} – ${course.usgaSeason.endMonthDay}`}.
              </Typography>
            )}

            {/* USGA Data Import (god only) */}
            {isGod && !isNew && (
              <Stack spacing={2}>
                <Divider />
                <Typography variant="titleMedium">USGA Data Import</Typography>
                {course.usgaCourseId ? (
                  <Stack spacing={1}>
                    <Typography variant="bodySmall" color="text.secondary">
                      Linked {HANDICAP_LABELS.COURSE_ID}: {course.usgaCourseId}
                    </Typography>
                    <Typography variant="bodySmall" color="text.secondary">
                      Posting season: {!usgaSeason
                        ? 'not imported yet'
                        : usgaSeason.isAllYear
                          ? 'all year'
                          : `${usgaSeason.startMonthDay} – ${usgaSeason.endMonthDay}`}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={isUsgaImportRunning}
                        onClick={() => requestUsgaImport(course.itemId)}
                      >
                        {isUsgaImportRunning ? <CircularProgress size="1.5rem" /> : 'Update from USGA'}
                      </Button>
                      {usgaCourseIdInput === null && (
                        <Button
                          variant="text"
                          size="small"
                          disabled={isUsgaImportRunning}
                          onClick={() => setUsgaCourseIdInput(course.usgaCourseId ?? '')}
                        >
                          Change ID
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="bodySmall" color="text.secondary">
                    Not linked to a USGA course.
                  </Typography>
                )}
                {(!course.usgaCourseId || usgaCourseIdInput !== null) && (
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      label={HANDICAP_LABELS.COURSE_ID}
                      value={usgaCourseIdInput ?? ''}
                      onChange={(e) => setUsgaCourseIdInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleLinkUsgaCourse()}
                      slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'numeric' } }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!isUsgaCourseIdValid || isUsgaImportRunning}
                      onClick={handleLinkUsgaCourse}
                    >
                      Link
                    </Button>
                    {course.usgaCourseId && (
                      <Button variant="text" size="small" onClick={() => setUsgaCourseIdInput(null)}>
                        Cancel
                      </Button>
                    )}
                  </Stack>
                )}
                {usgaImport?.status === USGA_COURSE_IMPORT_STATUSES.DONE && (
                  <Alert severity={usgaImport.message ? 'warning' : 'success'}>
                    Imported {usgaImport.teeCount ?? 0} tee sets{usgaImport.message ? `, but ${usgaImport.message}` : ' and the posting season.'}
                  </Alert>
                )}
                {usgaImport?.status === USGA_COURSE_IMPORT_STATUSES.ERROR && (
                  <Alert severity="error">{usgaImport.message ?? 'The USGA import failed.'}</Alert>
                )}
                <CourseUsgaTeeView courseId={course.itemId} course={course} isAdmin={true} onTeeDataChange={handleTeeDataChange} />
              </Stack>
            )}

            {/* Delete action for existing courses */}
            {!isNew && handleDelete && (
              <Stack direction="row" spacing={2}>
                {!deleteConfirm && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                )}
                {deleteConfirm && (
                  <>
                    <Button size="small" color="error" onClick={() => handleDelete(course)}>
                      Confirm Delete
                    </Button>
                    <Button size="small" onClick={() => setDeleteConfirm(false)}>
                      Cancel
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      );
    } else {
      return <></>;
    }
  } else {
    return <></>;
  }
}
